import * as THREE from "three";
import type { AssetPipeline } from "../core/assets";
import { addIndustrialAssetSites } from "./industrial-assets";
import { LANDMARK_LAYOUT } from "./layout";

type HeightFn = (x: number, z: number) => number;

const MAT = {
  tank: new THREE.MeshStandardMaterial({
    color: 0x5a605f,
    roughness: 0.55,
    metalness: 0.35,
    emissive: new THREE.Color(0x0d1113),
    emissiveIntensity: 0.22,
  }),
  concrete: new THREE.MeshStandardMaterial({
    color: 0x6d6b62,
    roughness: 0.92,
    emissive: new THREE.Color(0x11100d),
    emissiveIntensity: 0.12,
  }),
  steel: new THREE.MeshStandardMaterial({
    color: 0x616867,
    roughness: 0.38,
    metalness: 0.68,
    emissive: new THREE.Color(0x0b1011),
    emissiveIntensity: 0.18,
  }),
  darkSteel: new THREE.MeshStandardMaterial({
    color: 0x3f4547,
    roughness: 0.42,
    metalness: 0.52,
    emissive: new THREE.Color(0x070c0d),
    emissiveIntensity: 0.16,
  }),
  asphalt: new THREE.MeshStandardMaterial({
    color: 0x41423d,
    roughness: 0.96,
    emissive: new THREE.Color(0x090907),
    emissiveIntensity: 0.08,
  }),
  dirt: new THREE.MeshStandardMaterial({
    color: 0x5e5138,
    roughness: 1,
    emissive: new THREE.Color(0x120f09),
    emissiveIntensity: 0.12,
  }),
  stripe: new THREE.MeshStandardMaterial({
    color: 0xb4aa8a,
    roughness: 0.9,
    emissive: new THREE.Color(0x18140b),
    emissiveIntensity: 0.08,
  }),
} as const;

function addMesh(
  group: THREE.Group,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  x: number,
  y: number,
  z: number,
  rotationY = 0,
): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(x, y, z);
  mesh.rotation.y = rotationY;
  group.add(mesh);
  return mesh;
}

function rotateOffset(x: number, z: number, rotation: number): { x: number; z: number } {
  return {
    x: x * Math.cos(rotation) - z * Math.sin(rotation),
    z: x * Math.sin(rotation) + z * Math.cos(rotation),
  };
}

function addPad(group: THREE.Group, getHeight: HeightFn, x: number, z: number, width: number, depth: number, rotation = 0, material: THREE.Material = MAT.asphalt): number {
  const y = getHeight(x, z) + 0.08;
  addMesh(group, new THREE.BoxGeometry(width, 0.16, depth), material, x, y, z, rotation);
  return y;
}

function addFenceLoop(group: THREE.Group, getHeight: HeightFn, x: number, z: number, width: number, depth: number, rotation = 0): void {
  const halfW = width * 0.5;
  const halfD = depth * 0.5;
  const segments: Array<[number, number, number, number]> = [
    [0, -halfD, width, 0.24],
    [0, halfD, width, 0.24],
    [-halfW, 0, 0.24, depth],
    [halfW, 0, 0.24, depth],
  ];

  for (const [lx, lz, w, d] of segments) {
    const p = rotateOffset(lx, lz, rotation);
    const px = x + p.x;
    const pz = z + p.z;
    const py = getHeight(px, pz) + 0.75;
    addMesh(group, new THREE.BoxGeometry(w, 1.3, d), MAT.darkSteel, px, py, pz, rotation);
  }
}

function addContainer(group: THREE.Group, getHeight: HeightFn, x: number, z: number, rotation: number, tint = 0): void {
  const y = getHeight(x, z) + 1.35;
  const mat = MAT.darkSteel.clone();
  mat.color.offsetHSL(tint, 0.02, 0.03);
  addMesh(group, new THREE.BoxGeometry(5.8, 2.7, 2.4), mat, x, y, z, rotation);
}

function addVehicle(group: THREE.Group, getHeight: HeightFn, x: number, z: number, rotation: number, scale = 1): void {
  const y = getHeight(x, z);
  const body = addMesh(group, new THREE.BoxGeometry(4.6 * scale, 1.1 * scale, 2.1 * scale), MAT.steel, x, y + 0.95 * scale, z, rotation);
  body.castShadow = true;
  const cabOffset = rotateOffset(1.1 * scale, 0, rotation);
  addMesh(group, new THREE.BoxGeometry(1.5 * scale, 1.2 * scale, 1.8 * scale), MAT.concrete, x + cabOffset.x, y + 1.45 * scale, z + cabOffset.z, rotation);
  for (const [lx, lz] of [[1.4, 1], [1.4, -1], [-1.5, 1], [-1.5, -1]] as const) {
    const p = rotateOffset(lx * scale, lz * scale, rotation);
    addMesh(group, new THREE.CylinderGeometry(0.34 * scale, 0.34 * scale, 0.42 * scale, 10), MAT.darkSteel, x + p.x, y + 0.34 * scale, z + p.z, Math.PI * 0.5);
  }
}

function addPipeRack(group: THREE.Group, getHeight: HeightFn, x: number, z: number, length: number, rotation: number): void {
  const y = getHeight(x, z);
  for (const offset of [-0.9, 0, 0.9] as const) {
    const p = rotateOffset(0, offset, rotation);
    const pipe = addMesh(group, new THREE.CylinderGeometry(0.2, 0.2, length, 8), MAT.steel, x + p.x, y + 2.7, z + p.z, rotation);
    pipe.rotation.z = Math.PI * 0.5;
  }
  for (const lx of [-length * 0.32, 0, length * 0.32] as const) {
    const p = rotateOffset(lx, 0, rotation);
    addMesh(group, new THREE.BoxGeometry(0.24, 3.4, 2.8), MAT.darkSteel, x + p.x, y + 1.7, z + p.z, rotation);
  }
}

function addWaterTower(group: THREE.Group, getHeight: HeightFn, x: number, z: number): void {
  const y = getHeight(x, z);
  const tower = new THREE.Group();

  for (const [dx, dz] of [[-3, -3], [3, -3], [-3, 3], [3, 3]] as const) {
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.32, 15, 6), MAT.tank);
    leg.position.set(x + dx, y + 7.5, z + dz);
    tower.add(leg);
  }

  for (const height of [5, 10.5] as const) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(4.2, 0.12, 4, 12), MAT.concrete);
    ring.position.set(x, y + height, z);
    ring.rotation.x = Math.PI / 2;
    tower.add(ring);
  }

  const catwalk = new THREE.Mesh(new THREE.CylinderGeometry(5.6, 5.6, 0.22, 18), MAT.concrete);
  catwalk.position.set(x, y + 15.2, z);
  tower.add(catwalk);

  const bowl = new THREE.Mesh(new THREE.CylinderGeometry(4.8, 5.8, 4.8, 14, 1), MAT.tank);
  bowl.position.set(x, y + 17.8, z);
  tower.add(bowl);

  const cap = new THREE.Mesh(new THREE.ConeGeometry(5.2, 2.4, 14), MAT.tank);
  cap.position.set(x, y + 21.2, z);
  tower.add(cap);

  const hatch = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.3, 1.2), MAT.concrete);
  hatch.position.set(x + 1.8, y + 22.4, 0.5 + z);
  tower.add(hatch);

  group.add(tower);
}

function addRelayTower(group: THREE.Group, getHeight: HeightFn, x: number, z: number): void {
  const y = getHeight(x, z) + 0.1;
  const tower = new THREE.Group();

  for (const [dx, dz] of [[-2.3, -2.3], [2.3, -2.3], [-2.3, 2.3], [2.3, 2.3]] as const) {
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.28, 28, 6), MAT.tank);
    leg.position.set(x + dx, y + 14, z + dz);
    tower.add(leg);
  }

  for (const level of [8, 16, 24] as const) {
    const platform = new THREE.Mesh(new THREE.BoxGeometry(6.1, 0.18, 6.1), MAT.concrete);
    platform.position.set(x, y + level, z);
    tower.add(platform);

    const ring = new THREE.Mesh(new THREE.TorusGeometry(2.8, 0.12, 5, 12), MAT.tank);
    ring.position.set(x, y + level + 0.65, z);
    ring.rotation.x = Math.PI / 2;
    tower.add(ring);
  }

  for (const [ax, az, bx, bz] of [
    [-2.3, -2.3, 2.3, 2.3],
    [2.3, -2.3, -2.3, 2.3],
  ] as const) {
    const brace = new THREE.Mesh(new THREE.BoxGeometry(0.15, 28.5, 0.15), MAT.tank);
    brace.position.set(x + (ax + bx) * 0.5, y + 14, z + (az + bz) * 0.5);
    brace.rotation.z = Math.atan2(ax - bx, 28.5);
    brace.rotation.x = Math.atan2(az - bz, 28.5);
    tower.add(brace);
  }

  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.42, 34, 8), MAT.concrete);
  mast.position.set(x, y + 17, z);
  tower.add(mast);

  const antenna = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, 9, 6), MAT.tank);
  antenna.position.set(x, y + 38.5, z);
  tower.add(antenna);

  group.add(tower);
}

function addStorageTankCluster(group: THREE.Group, getHeight: HeightFn, x: number, z: number): void {
  addFenceLoop(group, getHeight, x + 1, z + 2, 40, 30);
  const yardY = addPad(group, getHeight, x + 1, z + 2, 36, 24, 0, MAT.concrete);

  const offsets = [[0, 0], [10, 4], [-9, 8]] as const;
  for (const [dx, dz] of offsets) {
    const px = x + dx;
    const pz = z + dz;
    const y = getHeight(px, pz);
    const tank = new THREE.Mesh(new THREE.CylinderGeometry(4.8, 4.8, 5.5, 18), MAT.tank);
    tank.position.set(px, y + 2.75, pz);
    group.add(tank);

    const roof = new THREE.Mesh(new THREE.ConeGeometry(5.2, 1.4, 18), MAT.concrete);
    roof.position.set(px, y + 6.2, pz);
    group.add(roof);
  }

  addPipeRack(group, getHeight, x + 0.5, z + 4, 20, 0);

  const control = new THREE.Mesh(new THREE.BoxGeometry(4.5, 3.4, 3.6), MAT.concrete);
  control.position.set(x - 16, getHeight(x - 16, z + 1.5) + 1.7, z + 1.5);
  group.add(control);

  const canopy = new THREE.Mesh(new THREE.BoxGeometry(5.2, 0.25, 4.2), MAT.steel);
  canopy.position.set(x - 16, getHeight(x - 16, z + 1.5) + 3.55, z + 1.5);
  group.add(canopy);

  addContainer(group, getHeight, x - 13.5, z - 9, 0.04, -0.02);
  addContainer(group, getHeight, x + 15.5, z - 8.5, -0.02, 0.04);
  addVehicle(group, getHeight, x - 9, z + 11, 0.2, 0.95);
  addVehicle(group, getHeight, x + 13, z + 11.5, -0.1, 0.82);

  const stripe = addMesh(group, new THREE.BoxGeometry(18, 0.03, 0.22), MAT.stripe, x - 2, yardY + 0.11, z - 12.2);
  stripe.rotation.y = 0.04;
}

function addHangar(group: THREE.Group, getHeight: HeightFn, x: number, z: number, rotation: number): void {
  addFenceLoop(group, getHeight, x, z, 42, 30, rotation);
  addPad(group, getHeight, x, z, 34, 22, rotation);
  const y = getHeight(x, z);
  const hangar = new THREE.Group();

  const shell = new THREE.Mesh(new THREE.BoxGeometry(18, 7.5, 12), MAT.concrete);
  shell.position.set(x, y + 3.75, z);
  shell.rotation.y = rotation;
  hangar.add(shell);

  const roof = new THREE.Mesh(new THREE.BoxGeometry(19.4, 0.45, 13.4), MAT.steel);
  roof.position.set(x, y + 7.7, z);
  roof.rotation.y = rotation;
  hangar.add(roof);

  const door = new THREE.Mesh(new THREE.BoxGeometry(0.4, 5.8, 8.8), MAT.darkSteel);
  door.position.set(x + Math.cos(rotation) * 8.8, y + 2.9, z + Math.sin(rotation) * 8.8);
  door.rotation.y = rotation;
  hangar.add(door);

  const annex = rotateOffset(-11, 0, rotation);
  addMesh(hangar, new THREE.BoxGeometry(6.4, 4.2, 5.4), MAT.concrete, x + annex.x, y + 2.1, z + annex.z, rotation);
  addMesh(hangar, new THREE.BoxGeometry(7.1, 0.25, 6), MAT.steel, x + annex.x, y + 4.35, z + annex.z, rotation);

  const awning = rotateOffset(0, -9.6, rotation);
  addMesh(hangar, new THREE.BoxGeometry(10, 0.22, 3.4), MAT.steel, x + awning.x, y + 3.2, z + awning.z, rotation);

  group.add(hangar);

  const truck = rotateOffset(4.5, -8.5, rotation);
  addVehicle(group, getHeight, x + truck.x, z + truck.z, rotation + Math.PI * 0.5, 1);
  const fuelPod = rotateOffset(-12, -8, rotation);
  addMesh(group, new THREE.CylinderGeometry(1.1, 1.1, 4.4, 10), MAT.tank, x + fuelPod.x, getHeight(x + fuelPod.x, z + fuelPod.z) + 1.1, z + fuelPod.z, rotation + Math.PI * 0.5);
  addContainer(group, getHeight, x + fuelPod.x + Math.sin(rotation) * 5, z + fuelPod.z - Math.cos(rotation) * 5, rotation + 0.1, 0.08);
}

function addMotorPool(group: THREE.Group, getHeight: HeightFn, x: number, z: number, rotation: number): void {
  addFenceLoop(group, getHeight, x, z, 54, 34, rotation);
  addPad(group, getHeight, x, z, 48, 28, rotation, MAT.dirt);

  const hallA = rotateOffset(-11, -7, rotation);
  const hallB = rotateOffset(11, 6, rotation);
  addMesh(group, new THREE.BoxGeometry(14, 5.5, 8.4), MAT.concrete, x + hallA.x, getHeight(x + hallA.x, z + hallA.z) + 2.75, z + hallA.z, rotation);
  addMesh(group, new THREE.BoxGeometry(14.8, 0.28, 9), MAT.steel, x + hallA.x, getHeight(x + hallA.x, z + hallA.z) + 5.8, z + hallA.z, rotation);
  addMesh(group, new THREE.BoxGeometry(16, 5.8, 9), MAT.concrete, x + hallB.x, getHeight(x + hallB.x, z + hallB.z) + 2.9, z + hallB.z, rotation);
  addMesh(group, new THREE.BoxGeometry(16.8, 0.28, 9.6), MAT.steel, x + hallB.x, getHeight(x + hallB.x, z + hallB.z) + 6.05, z + hallB.z, rotation);

  for (const [lx, lz, r] of [
    [-16, 9, 0.22],
    [-7, 10.5, 0.1],
    [3, 9.5, -0.04],
    [14, 10.5, 0.18],
  ] as const) {
    const p = rotateOffset(lx, lz, rotation);
    addVehicle(group, getHeight, x + p.x, z + p.z, rotation + r, 0.94);
  }

  const tower = rotateOffset(20, -11, rotation);
  addMesh(group, new THREE.BoxGeometry(3.8, 9, 3.8), MAT.concrete, x + tower.x, getHeight(x + tower.x, z + tower.z) + 4.5, z + tower.z, rotation);
  addMesh(group, new THREE.BoxGeometry(4.6, 0.25, 4.6), MAT.steel, x + tower.x, getHeight(x + tower.x, z + tower.z) + 9.15, z + tower.z, rotation);

  const containerA = rotateOffset(18, 7, rotation);
  const containerB = rotateOffset(18, 11, rotation);
  addContainer(group, getHeight, x + containerA.x, z + containerA.z, rotation + 0.04, -0.05);
  addContainer(group, getHeight, x + containerB.x, z + containerB.z, rotation + 0.04, 0.06);
}

function addSubstation(group: THREE.Group, getHeight: HeightFn, x: number, z: number, rotation: number): void {
  addFenceLoop(group, getHeight, x, z, 28, 24, rotation);
  addPad(group, getHeight, x, z, 22, 18, rotation, MAT.concrete);

  for (const [lx, lz] of [[-6, -4], [0, -4], [6, -4], [-6, 4], [0, 4], [6, 4]] as const) {
    const p = rotateOffset(lx, lz, rotation);
    const px = x + p.x;
    const pz = z + p.z;
    const y = getHeight(px, pz);
    addMesh(group, new THREE.BoxGeometry(2.4, 2.2, 1.8), MAT.steel, px, y + 1.1, pz, rotation);
    addMesh(group, new THREE.BoxGeometry(2.8, 0.18, 2.2), MAT.darkSteel, px, y + 2.35, pz, rotation);
  }

  for (const [lx, lz] of [[-9, -7], [9, -7], [-9, 7], [9, 7]] as const) {
    const p = rotateOffset(lx, lz, rotation);
    addMesh(group, new THREE.CylinderGeometry(0.12, 0.12, 5.2, 6), MAT.darkSteel, x + p.x, getHeight(x + p.x, z + p.z) + 2.6, z + p.z);
  }

  addPipeRack(group, getHeight, x, z, 16, rotation);
}

export function addLandmarkKit(group: THREE.Group, getHeight: HeightFn, assets?: AssetPipeline): void {
  addWaterTower(group, getHeight, LANDMARK_LAYOUT.waterTower.x, LANDMARK_LAYOUT.waterTower.z);
  addRelayTower(group, getHeight, LANDMARK_LAYOUT.relayTower.x, LANDMARK_LAYOUT.relayTower.z);
  addStorageTankCluster(group, getHeight, LANDMARK_LAYOUT.storageTanks.x, LANDMARK_LAYOUT.storageTanks.z);

  if (assets) {
    void addIndustrialAssetSites(group, assets, getHeight).catch((error) => {
      console.warn("Falling back to primitive industrial sites", error);
      for (const hangar of LANDMARK_LAYOUT.hangars) {
        addHangar(group, getHeight, hangar.x, hangar.z, hangar.rotation);
      }
      addMotorPool(group, getHeight, LANDMARK_LAYOUT.motorPool.x, LANDMARK_LAYOUT.motorPool.z, LANDMARK_LAYOUT.motorPool.rotation);
      addSubstation(group, getHeight, LANDMARK_LAYOUT.substation.x, LANDMARK_LAYOUT.substation.z, LANDMARK_LAYOUT.substation.rotation);
    });
  } else {
    for (const hangar of LANDMARK_LAYOUT.hangars) {
      addHangar(group, getHeight, hangar.x, hangar.z, hangar.rotation);
    }

    addMotorPool(group, getHeight, LANDMARK_LAYOUT.motorPool.x, LANDMARK_LAYOUT.motorPool.z, LANDMARK_LAYOUT.motorPool.rotation);
    addSubstation(group, getHeight, LANDMARK_LAYOUT.substation.x, LANDMARK_LAYOUT.substation.z, LANDMARK_LAYOUT.substation.rotation);
  }
}
